import { Injectable, inject } from '@angular/core';
import { LocalStorageService } from '@services/local-storage.service';

export type WalletEventChannel = 'push' | 'socket';

@Injectable({
  providedIn: 'root'
})
export class WalletEventDeduplicationService {
  private readonly localStorage: LocalStorageService = inject(LocalStorageService);
  private readonly storageKey: string = 'processedWalletEventIds';
  private readonly maxStoredEvents: number = 100;
  private readonly processedEventIds: Set<string> = new Set<string>();

  public constructor() {
    for (const eventId of this.readStoredEventIds()) {
      this.processedEventIds.add(eventId);
    }
  }

  public claim(channel: WalletEventChannel, eventId: unknown): boolean {
    const eventKey: string | null = this.getEventKey(channel, eventId);
    if (eventKey === null) return true;
    if (this.processedEventIds.has(eventKey)) return false;

    this.processedEventIds.add(eventKey);
    while (this.processedEventIds.size > this.maxStoredEvents) {
      const oldestEventId: string | undefined = this.processedEventIds.values().next().value;
      if (oldestEventId === undefined) break;
      this.processedEventIds.delete(oldestEventId);
    }
    this.persist();
    return true;
  }

  public release(channel: WalletEventChannel, eventId: unknown): void {
    const eventKey: string | null = this.getEventKey(channel, eventId);
    if (eventKey === null || !this.processedEventIds.delete(eventKey)) return;
    this.persist();
  }

  private getEventKey(channel: WalletEventChannel, eventId: unknown): string | null {
    if (typeof eventId !== 'string') return null;
    const normalizedEventId: string = eventId.trim();
    return normalizedEventId.length > 0 ? `${channel}:${normalizedEventId}` : null;
  }

  private readStoredEventIds(): string[] {
    try {
      const storedValue: unknown = this.localStorage.getItem(this.storageKey);
      if (typeof storedValue !== 'string') return [];
      const parsedValue: unknown = JSON.parse(storedValue);
      if (!Array.isArray(parsedValue)) return [];
      return parsedValue
        .filter((value: unknown): value is string => typeof value === 'string')
        .slice(-this.maxStoredEvents);
    } catch {
      return [];
    }
  }

  private persist(): void {
    try {
      this.localStorage.setItem(this.storageKey, Array.from(this.processedEventIds));
    } catch {
      // In-memory deduplication still works when browser storage is unavailable.
    }
  }
}
